export type Rng = () => number;

export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
export const damp = (current: number, target: number, lambda: number, dt: number) =>
  lerp(current, target, 1 - Math.exp(-lambda * dt));

const GRAD3 = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [-1, 1], [1, -1], [-1, -1],
];
const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;

/** 2D simplex noise (Gustavson), output roughly in [-1, 1]. */
export class Simplex2 {
  private readonly perm = new Uint8Array(512);
  private readonly permMod12 = new Uint8Array(512);

  constructor(seed: number) {
    const rng = mulberry32(seed);
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = p[i];
      p[i] = p[j];
      p[j] = tmp;
    }
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255];
      this.permMod12[i] = this.perm[i] % 12;
    }
  }

  noise(xin: number, yin: number): number {
    const perm = this.perm;
    const pm = this.permMod12;
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const t = (i + j) * G2;
    const x0 = xin - (i - t);
    const y0 = yin - (j - t);
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;
    const ii = i & 255;
    const jj = j & 255;
    let n0 = 0;
    let n1 = 0;
    let n2 = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 >= 0) {
      const g = GRAD3[pm[ii + perm[jj]]];
      t0 *= t0;
      n0 = t0 * t0 * (g[0] * x0 + g[1] * y0);
    }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 >= 0) {
      const g = GRAD3[pm[ii + i1 + perm[jj + j1]]];
      t1 *= t1;
      n1 = t1 * t1 * (g[0] * x1 + g[1] * y1);
    }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 >= 0) {
      const g = GRAD3[pm[ii + 1 + perm[jj + 1]]];
      t2 *= t2;
      n2 = t2 * t2 * (g[0] * x2 + g[1] * y2);
    }
    return 70 * (n0 + n1 + n2);
  }

  fbm(x: number, y: number, octaves: number, lacunarity = 2, gain = 0.5): number {
    let sum = 0;
    let amp = 1;
    let norm = 0;
    let fx = x;
    let fy = y;
    for (let o = 0; o < octaves; o++) {
      sum += amp * this.noise(fx, fy);
      norm += amp;
      amp *= gain;
      // Rotate each octave slightly to hide lattice alignment.
      const rx = fx * 1.6 - fy * 1.2;
      const ry = fx * 1.2 + fy * 1.6;
      fx = rx * (lacunarity / 2) + 17.1;
      fy = ry * (lacunarity / 2) - 9.3;
    }
    return sum / norm;
  }
}

/** Periodic gradient noise so the generated texture tiles seamlessly. */
function periodicPerlin(x: number, y: number, period: number, perm: Uint8Array): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * xf * (xf * (xf * 6 - 15) + 10);
  const v = yf * yf * yf * (yf * (yf * 6 - 15) + 10);
  const grad = (ix: number, iy: number, dx: number, dy: number) => {
    const px = ((ix % period) + period) % period;
    const py = ((iy % period) + period) % period;
    const h = perm[(perm[px & 255] + py) & 255] & 7;
    const gx = h < 4 ? (h & 1 ? -1 : 1) : 0;
    const gy = h < 4 ? (h & 2 ? -1 : 1) : h & 1 ? -1 : 1;
    return gx * dx + gy * dy;
  };
  const n00 = grad(xi, yi, xf, yf);
  const n10 = grad(xi + 1, yi, xf - 1, yf);
  const n01 = grad(xi, yi + 1, xf, yf - 1);
  const n11 = grad(xi + 1, yi + 1, xf - 1, yf - 1);
  const nx0 = n00 + (n10 - n00) * u;
  const nx1 = n01 + (n11 - n01) * u;
  return nx0 + (nx1 - nx0) * v;
}

export const NOISE_SIZE = 256;

/**
 * Tileable RGBA noise shared by the GPU (as a texture) and CPU (bilinear mirror).
 * R: broad soft gusts, G: mid patches, B: fine detail, A: alternate broad field.
 */
export class NoiseField {
  readonly data: Uint8Array;
  readonly size = NOISE_SIZE;

  constructor(seed: number) {
    const size = NOISE_SIZE;
    const rng = mulberry32(seed);
    const makePerm = () => {
      const p = new Uint8Array(256);
      for (let i = 0; i < 256; i++) p[i] = i;
      for (let i = 255; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        const tmp = p[i];
        p[i] = p[j];
        p[j] = tmp;
      }
      return p;
    };
    const channels: Array<{ base: number; octaves: number; gain: number }> = [
      { base: 4, octaves: 4, gain: 0.5 },
      { base: 8, octaves: 4, gain: 0.55 },
      { base: 16, octaves: 3, gain: 0.5 },
      { base: 6, octaves: 3, gain: 0.5 },
    ];
    this.data = new Uint8Array(size * size * 4);
    const tmp = new Float32Array(size * size);
    channels.forEach((cfg, ch) => {
      const perm = makePerm();
      let min = Infinity;
      let max = -Infinity;
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          let sum = 0;
          let amp = 1;
          let norm = 0;
          let period = cfg.base;
          for (let o = 0; o < cfg.octaves; o++) {
            sum += amp * periodicPerlin((x / size) * period, (y / size) * period, period, perm);
            norm += amp;
            amp *= cfg.gain;
            period *= 2;
          }
          const value = sum / norm;
          tmp[y * size + x] = value;
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
      const range = max - min || 1;
      for (let i = 0; i < size * size; i++) {
        this.data[i * 4 + ch] = Math.round(((tmp[i] - min) / range) * 255);
      }
    });
  }

  /** Bilinear sample matching GPU LINEAR filtering with REPEAT wrapping. Returns 0..1. */
  sample(u: number, v: number, channel: number): number {
    const size = this.size;
    const x = u * size - 0.5;
    const y = v * size - 0.5;
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const fx = x - x0;
    const fy = y - y0;
    const wrap = (n: number) => ((n % size) + size) % size;
    const ix0 = wrap(x0);
    const ix1 = wrap(x0 + 1);
    const iy0 = wrap(y0);
    const iy1 = wrap(y0 + 1);
    const d = this.data;
    const a = d[(iy0 * size + ix0) * 4 + channel];
    const b = d[(iy0 * size + ix1) * 4 + channel];
    const c = d[(iy1 * size + ix0) * 4 + channel];
    const e = d[(iy1 * size + ix1) * 4 + channel];
    return ((a + (b - a) * fx) * (1 - fy) + (c + (e - c) * fx) * fy) / 255;
  }
}
